import { Component, ElementRef, EventEmitter, Input, OnInit, OnDestroy, Output, ViewChild } from '@angular/core';
import { marked } from 'marked';

import { GfTitleService } from '../../../core/services/core.title.service';
import { BreadcrumbService } from '../../../widgets/breadcrumbs/breadcrumbs.service';
import { TagsModel } from '../../../core/services/core.models.tags.component';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { UploadService } from '../../../core/services/core.upload.service';
import {
    generateRoleSlug, MentionConfig, MentionRole, MentionsService
} from '../../../core/services/core.mentio.service';
import { ProjectService } from '../../../core/services/project.service';
import { NotificationService } from '../../../core/services/core.notification.service';
import { DragNotificationService } from '../../../core/services/core.drag-notification.service';
import { WikiMarkdownEditorComponent } from '../../../widgets/wiki/edit-markdown/wiki.markdown-editor.component';
import { WikiImportResult } from '../../../widgets/wiki/import/wiki.import.component';
import { WikiEditLifecycleService, WikiEditLifecycleInstance } from '../../../widgets/wiki/services/wiki.edit-lifecycle.service';
import { WikiService } from '../../../widgets/wiki/services/wiki.service';
import { WikiAttachmentQueueService } from '../attachments/wiki.attachment-queue.service';
import { WikiAttachmentPickerModalComponent } from '../attachments/wiki.attachment-picker-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { escapeHtml } from '../../../core/services/core.utils.service';
import { AiFlowService } from '../../../core/services/core.ai-flow.service';
import { StringDiffService } from '../../../core/services/core.string-diff.service';

import * as _ from 'lodash';
import { marker } from '../../../core/services/ngx-translate-extract-marker';

@Component({
    selector: 'wiki-edit-view',
    template: require('./wiki.edit-view.component.html')
})
export class WikiEditViewComponent implements OnInit, OnDestroy {
    @ViewChild('markdownEditor', { static: false }) markdownEditor!: WikiMarkdownEditorComponent;
    @ViewChild('fileInput', { static: false }) fileInputRef: ElementRef;

    @Input() project: any = null;
    @Input() wikiPage: any = null;
    @Input() attachmentCount: number = 0;

    // ─── Embedding configuration (defaults target the project wiki surface) ───
    /** Save scope — when set, saves target `(section, refId)` instead of `project.unixName`. */
    @Input() section: string = '';
    @Input() refId: number = 0;
    /** Show the tag editor (wiki on, homepages off). */
    @Input() showTags: boolean = true;
    /** Show the attachment bar + drag/drop (wiki on, homepages off). */
    @Input() showAttachments: boolean = true;
    /** Manage page title + breadcrumbs (wiki on; homepages own their chrome). */
    @Input() manageChrome: boolean = true;
    /** UI-Router states the save lifecycle hooks/navigates against. */
    @Input() editState: string = 'home.project.summary.wiki.page.edit';
    @Input() reloadState: string = 'home.project.summary.wiki.page';
    @Input() dismissState: string = 'home.project.summary.wiki.page';
    /**
     * Register the save-on-leave UI-Router transition hook. Set false when an embedding host
     * (e.g. <wiki-container>) drives save-on-leave itself — it then reads {@link hasContentChanges}
     * and calls `wikiEditorLifecycle.saveMarkdownContent()` on its own transition boundary.
     */
    @Input() routeHook: boolean = true;
    /**
     * Show the editor toolbar's Rename button. Off by default: a host that pins the page name
     * (the project homepage, the sprint retrospective) has no meaningful rename, and neither
     * does a page that does not exist yet — it takes its name from the create flow.
     */
    @Input() allowRename: boolean = false;

    /**
     * Asks the host to start a rename. The editor neither collects the new name nor performs
     * the rename: the host owns the dialog that does both, the pending-content save that has
     * to happen first, and the route change that follows.
     */
    @Output() renameRequested = new EventEmitter<void>();

    private tagsOriginal: any = [];
    tags: any = [];

    /** Optional short note describing this edit, sent with the next save and shown in the
     *  Versions tab. Cleared after each save (see setSavePayloadExtra call sites). */
    changeSummary: string = '';

    /** True while an AI-generated change summary is being requested — see onGenerateChangeSummary. */
    generatingSummary: boolean = false;
    /** Set when the last generate-summary request failed or found nothing to summarize. */
    generateSummaryError: string | null = null;

    /** @-mention source for the editor. Null until ngOnInit builds it. */
    mentions: MentionConfig | null = null;

    /** Project roles offered alongside users; filled in asynchronously. */
    private projectRoles: MentionRole[] = [];

    wikiEditorLifecycle!: WikiEditLifecycleInstance;

    // Expose lifecycle state for template bindings
    get pageName(): string { return this.wikiEditorLifecycle.pageName; }
    get wikiId(): number { return this.wikiEditorLifecycle.wikiId; }
    get contentFormat() { return this.wikiEditorLifecycle.content_type; }
    get isConverting() { return this.wikiEditorLifecycle.isConverting; }
    get wasConvertedFromHtml() { return this.wikiEditorLifecycle.wasConvertedFromHtml; }
    get editableContent() { return this.wikiEditorLifecycle.editableContent; }
    get contentReady() { return this.wikiEditorLifecycle.contentReady; }
    get conversionQuality() { return this.wikiEditorLifecycle.conversionQuality; }
    get conversionOriginalHtml() { return this.wikiEditorLifecycle.conversionOriginalHtml; }
    get conversionWarnings() { return this.wikiEditorLifecycle.conversionWarnings; }

    /** Whether there's a saved prior version to diff against — hides the Generate button on a brand-new page. */
    get canGenerateChangeSummary(): boolean { return this.wikiId > 0; }

    draggingOver: boolean = false;
    stagedFiles: File[] = [];
    stagedFilesDisplay: any[] = [];
    private stagedFileIds: number[] = [];
    /** Imports applied in this edit; their pictures are pending until a save. */
    private pendingImportIds: string[] = [];

    attachmentBarOpen: boolean = false;
    showSideBySide: boolean = false;
    showHelpPanel: boolean = false;
    helpSidebarTab: string = 'macros';
    helpSidebarWidth: number = 420;

    private readonly helpSidebarMinWidth: number = 320;
    private readonly helpSidebarMaxWidth: number = 640;
    private readonly helpSidebarWidthStorageKey: string = 'wiki.help.sidebar.width';
    private readonly attachmentBarOpenStorageKey: string = 'wiki.attachment.bar.open';
    private attachmentCountSub: any;
    private isResizingHelpSidebar: boolean = false;
    private removeResizeListeners: (() => void) | null = null;

    get totalAttachmentCount(): number {
        return this.attachmentCount + this.stagedFilesDisplay.length;
    }

    get convertedMarkdownHtml(): string {
        try {
            let html = marked.parse(this.editableContent || '') as string;
            html = this.applyBootstrapTableClasses(html);
            html = this.convertVideoLinks(html);
            return html;
        } catch {
            return this.editableContent || '';
        }
    }

    dismissConversionAlert(): void {
        this.wikiEditorLifecycle.conversionAlertDismissed = true;
    }

    /**
     * Accept the auto-converted markdown and save the page.
     * Delegates to the markdown editor's save trigger, which routes through
     * the normal save pipeline. On success, `wasConvertedFromHtml` is cleared
     * by the lifecycle service and this banner hides automatically.
     */
    acceptConversion(): void {
        if (this.markdownEditor) {
            this.markdownEditor.triggerSave();
        }
    }

    /**
     * Convert <img> tags and standalone <a> links pointing to video URLs
     * into embedded video players.
     * Supports direct video files (.mp4, .webm, .ogg, .mov), YouTube, and Vimeo.
     */
    private convertVideoLinks(html: string): string {
        // Pass 1: Convert <img> tags with video URLs
        html = html.replace(/<img\s+[^>]*src=["']([^"']+)["'][^>]*>/gi, (fullTag, src) => {
            const altMatch = fullTag.match(/alt=["']([^"']*)["']/);
            const alt = altMatch ? altMatch[1] : '';
            return this.buildVideoEmbed(src, alt) || fullTag;
        });

        // Pass 2: Convert standalone links to video URLs
        // Matches <p><a href="url">text</a></p> where the link is the only content
        html = html.replace(/<p>\s*<a\s+href=["']([^"']+)["'][^>]*>[^<]*<\/a>\s*<\/p>/gi, (fullTag, href) => {
            return this.buildVideoEmbed(href) || fullTag;
        });

        return html;
    }

    private buildVideoEmbed(url: string, alt: string = ''): string | null {
        const caption = alt ? `<p class="wiki-video-caption">${escapeHtml(alt)}</p>` : '';

        // Direct video files
        if (/\.(mp4|webm|ogg|mov)(\?[^"']*)?$/i.test(url)) {
            return `<div class="wiki-video-container"><video controls preload="metadata" class="wiki-video-player"><source src="${escapeHtml(url)}">Your browser does not support the video tag.</video>${caption}</div>`;
        }

        // YouTube
        const ytMatch = url.match(/(?:youtube\.com\/watch\?.*v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
        if (ytMatch) {
            return `<div class="wiki-video-container"><iframe class="wiki-video-player" src="https://www.youtube-nocookie.com/embed/${escapeHtml(ytMatch[1])}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>${caption}</div>`;
        }

        // Vimeo
        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
            return `<div class="wiki-video-container"><iframe class="wiki-video-player" src="https://player.vimeo.com/video/${escapeHtml(vimeoMatch[1])}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>${caption}</div>`;
        }

        return null;
    }

    private applyBootstrapTableClasses(html: string): string {
        return html.replace(/<table\b([^>]*)>/gi, (fullTag, attrs) => {
            const requiredClasses = ['table', 'table-striped', 'table-hover'];
            const classMatch = attrs.match(/\sclass\s*=\s*(["'])(.*?)\1/i);

            if (classMatch) {
                const existingClasses = classMatch[2].trim().split(/\s+/).filter(Boolean);
                const mergedClasses = Array.from(new Set([...existingClasses, ...requiredClasses]));
                return fullTag.replace(classMatch[0], ` class=${classMatch[1]}${mergedClasses.join(' ')}${classMatch[1]}`);
            }

            return `<table class="${requiredClasses.join(' ')}"${attrs}>`;
        });
    }

    constructor(
        private breadcrumbService: BreadcrumbService,
        private tagsModel: TagsModel,
        private uiService: UiService,
        private titleService: GfTitleService,
        private translateService: GfTranslateService,
        private wikiEditorLifecycleService: WikiEditLifecycleService,
        private wikiService: WikiService,
        private uploadService: UploadService,
        private attachmentQueueService: WikiAttachmentQueueService,
        private notificationService: NotificationService,
        private dragNotificationService: DragNotificationService,
        private modalService: NgbModal,
        private mentionsService: MentionsService,
        private projectService: ProjectService,
        private aiFlowService: AiFlowService,
        private stringDiffService: StringDiffService,
    ) {}

    /** Ask the host to open the rename dialog for the page being edited. */
    requestRename(): void {
        this.renameRequested.emit();
    }

    private scope() {
        return this.section ? { section: this.section, refId: this.refId } : { project: this.project?.unixName };
    }

    ngOnInit() {
        // Transient, tied to the next save rather than to any loaded page state — reset so a
        // summary typed for one page can't leak onto another if this instance is reused.
        this.changeSummary = '';

        // Lifecycle is configured here (not the constructor) so the embedding @Inputs
        // (states, scope) are available. routeHook:true registers the onBefore transition
        // hook that auto-saves when the user leaves this surface's Edit state.
        this.wikiEditorLifecycle = this.wikiEditorLifecycleService.createInstance({
            fromState: this.editState,
            reloadState: this.reloadState,
            dismissState: this.dismissState,
            routeHook: this.routeHook,
        });
        this.wikiEditorLifecycle.setRefs(
            // Null until the editor has actually mounted: an unmounted editor
            // answers getMarkdown() with '', which against a stored page reads as
            // "the user deleted everything" and would let a save-on-leave during
            // startup persist a blank page. WikiEditService.getEditorMarkdown()
            // turns a null ref into a null reading, and hasContentChanges()
            // treats that as "no changes" rather than "emptied".
            () => this.markdownEditor?.isReady ? this.markdownEditor : null,
            () => this.wikiPage,
            () => this.scope(),
        );

        this.restoreHelpSidebarWidth();
        this.initializeMentionConfig();

        const pageName = _.get(this.wikiPage, 'pagename', 'FrontPage');

        if (this.manageChrome) {
            this.titleService.setTitle(_.get(this.project, 'name', ''), 'Wiki', pageName + ' - Edit');
            this.breadcrumbService.setCrumbs([
                this.breadcrumbService.projectCrumb(this.project),
                this.breadcrumbService.translatedCrumb(marker('Wiki'), 'home.project.summary.wiki', { pagename: 'FrontPage' }),
                this.breadcrumbService.stateCrumb(pageName, 'home.project.summary.wiki.page', { pagename: pageName }),
                this.breadcrumbService.translatedCrumb(marker('Edit'), '.', {})
            ]);
        }

        if (this.showTags) {
            // Read tags from the returned result, not the shared tagsModel singleton — that
            // field is unsafe when several tag widgets share this model instance.
            this.tagsModel.load('wikipage', _.get(this.wikiPage, 'id', 0))
                .then((result: any) => {
                    const loaded = (result && result.tags) ? result.tags : [];
                    this.tags = this.tagsModel.format(_.uniq(loaded));
                    this.tagsOriginal = _.clone(this.tags);
                });
        }

        // Set up dismissParams dynamically (needs pageName)
        this.wikiEditorLifecycle.setDismissParams({ pagename: pageName });

        this.wikiEditorLifecycle.init(pageName, this.wikiPage);

        const storedBarOpen = window.localStorage.getItem(this.attachmentBarOpenStorageKey);
        this.attachmentBarOpen = storedBarOpen === '1';

        // Claim staged attachments and (for existing pages) save tags on navigation away.
        this.wikiEditorLifecycle.registerBeforeLeaveHook(() => {
            const extra: any = {};
            let triggerImmediateSave = false;

            if (this.showAttachments && this.stagedFileIds.length > 0) {
                if (this.wikiId === 0 && !this.wikiEditorLifecycle.hasContentChanges()) {
                    // New page with no content — saving would create a blank page just to claim
                    // the uploads. Warn instead; files remain as orphaned records for server GC.
                    this.stagedFileIds = [];
                    this.stagedFilesDisplay = [];
                    this.uiService.message.showWarning(
                        this.translateService.translate('You uploaded files but haven\'t added any content. Your uploads will not be saved.'),
                        this.translateService.translate('Attachments')
                    );
                } else {
                    // Content exists or existing page — include IDs in save and update the count.
                    extra.attachmentIds = [...this.stagedFileIds];
                    this.wikiService.attachmentCount$.next(this.totalAttachmentCount);
                    this.stagedFileIds = [];
                    triggerImmediateSave = !this.wikiEditorLifecycle.hasContentChanges();
                }
            }

            if (this.changeSummary) {
                extra.changeSummary = this.changeSummary;
                this.changeSummary = '';
            }

            if (Object.keys(extra).length > 0) {
                this.wikiEditorLifecycle.setSavePayloadExtra(extra);
            }

            if (triggerImmediateSave) {
                // Existing page with no content changes — trigger an explicit save so the
                // attachments (and any pending change summary) still get attached to a version.
                this.wikiEditorLifecycle.saveMarkdownContent().catch(() => {});
            }

            // For existing pages save tags now (when the tags feature is enabled). New pages
            // (wikiId=0) defer to the afterSave hook below so tags are saved with the real ID —
            // the backend rejects refId=0.
            if (this.showTags && this.wikiId > 0) {
                return this.saveTagsIfChanged();
            }
        });

        this.dragNotificationService.initializeDragListeners();
        this.notificationService.subscribe('wiki-edit', 'DRAGOVER_START_EVENT', () => { this.draggingOver = true; });
        this.notificationService.subscribe('wiki-edit', 'DRAGOVER_END_EVENT', () => { this.draggingOver = false; });

        this.attachmentCountSub = this.wikiService.attachmentCount$.subscribe((count: number) => {
            this.attachmentCount = count;
        });

        // Upload any files staged on the Attachments tab once the new page gets its ID
        this.wikiEditorLifecycle.registerAfterSaveHook((newId: number) => this.uploadQueuedAttachments(newId));
        // Save tags with the real page ID once a new page is created (wikiId=0 is rejected by the
        // backend). Returning the promise lets the lifecycle await it before navigating away.
        this.wikiEditorLifecycle.registerAfterSaveHook((newId: number) => this.saveTagsIfChanged(newId));
    }

    /**
     * Build the @-mention source for this page, and start loading the project's
     * roles so `@role-slug` is offered alongside people.
     *
     * A wiki page has no roster of its own — no assignees, no thread
     * participants — so there is no `seed`: everyone comes from the project
     * members lookup. `minSearchLength: 0` lets that lookup run on an empty
     * query, so a bare '@' offers people alongside roles, as tracker does;
     * at the default of 2 the list was roles-only until the second character.
     * `project` is a thunk because the page can be rendered before its project
     * resolves.
     *
     * No @gforge either, unlike chat and tracker: that account exists to be
     * asked a question, and a wiki page has nowhere for it to reply.
     */
    private initializeMentionConfig(): void {
        this.mentions = this.mentionsService.createMentionConfig({
            project: () => _.get(this.project, 'unixName', ''),
            roles: () => this.projectRoles,
            search: { minSearchLength: 0 },
        });

        const unixName = _.get(this.project, 'unixName', '');
        if (!unixName) { return; }

        this.projectService.roles(unixName).then((response: any) => {
            this.projectRoles = _.get(response, 'items', [])
                .filter((role: any) => role.roleName)
                .map((role: any) => ({
                    roleSlug: role.roleSlug || generateRoleSlug(role.roleName),
                    roleName: role.roleName,
                }));
            // Roles arrive after the source was built; re-run so the very first
            // '@' offers them too.
            this.mentions?.refresh();
        }, () => {
            // A failed role lookup leaves user mentions working; nothing to do.
        });
    }

    ngOnDestroy() {
        this.mentions?.destroy();
        this.stopHelpSidebarResize();
        this.wikiEditorLifecycle.destroy();
        this.notificationService.unsubscribe('wiki-edit');
        this.dragNotificationService.destroy();
        if (this.attachmentCountSub) { this.attachmentCountSub.unsubscribe(); }
    }

    // ─────────────────── tags ────────────────────

    onFileDrop(event: DragEvent): void {
        event.preventDefault();
        this.draggingOver = false;
        if (!event.dataTransfer || !event.dataTransfer.files || event.dataTransfer.files.length === 0) { return; }
        this.stageFiles(Array.from(event.dataTransfer.files) as File[]);
    }

    triggerFileUpload(): void {
        this.fileInputRef.nativeElement.click();
    }

    onFileSelected(event: any): void {
        const raw: FileList = event.target.files;
        if (!raw || raw.length === 0) { return; }
        this.stageFiles(Array.from(raw) as File[]);
        event.target.value = '';
    }

    private stageFiles(files: File[]): void {
        if (this.wikiId > 0) {
            // Existing page — upload immediately with defer=true (ref_id stays NULL until save)
            files.forEach(f => this.stagedFilesDisplay.push({
                name: f.name, size: f.size, percentComplete: 0, amountComplete: 0, amountTotal: f.size
            }));
            const progressCallback = (_s: string, _r: number, file: any, bytesUploaded: number, bytesRemaining: number, percent: number) => {
                const entry = this.stagedFilesDisplay.find(f => f.name === file.name);
                if (entry) { entry.amountComplete = bytesUploaded; entry.amountTotal = bytesUploaded + bytesRemaining; entry.percentComplete = percent; }
            };
            const successCallback = (_s: string, _r: number, file: any, hash: any) => {
                return this.wikiService.setAttachment(this.wikiId, hash, {
                    fileName: file.name,
                    fileType: file.type || 'application/octet-stream'
                }, true).then((response: any) => {
                    const filesystemId: number = response?.filesystemId;
                    if (filesystemId) { this.stagedFileIds.push(filesystemId); }
                    const location: string = response?.location;
                    if (location) {
                        this.wikiService.getAttachmentMeta(location)
                            .then((meta: any) => {
                                const entry = this.stagedFilesDisplay.find(f => f.name === file.name);
                                if (entry) {
                                    entry.attachmentUrl = meta?.attachmentUrl;
                                    entry.postedBy     = meta?.postedBy;
                                    entry.uploadedOn   = meta?.uploadedOn;
                                    entry.filesystemId = filesystemId;
                                    entry.uploading    = false;
                                }
                            })
                            .catch(() => {});
                    }
                });
            };
            this.uploadService.uploadFiles('wikifiles', this.wikiId, files, { successCallback, progressCallback })
                .catch(() => this.uiService.message.showWarning(
                    this.translateService.translate('Could not upload one or more attachments'),
                    this.translateService.translate('Attachments')
                ));
        } else {
            // New page — upload immediately to the staged endpoint so the file gets a URL
            // that works before the page has a permanent ID.
            const projectId: number = this.project.id;
            files.forEach(f => this.stagedFilesDisplay.push({
                name: f.name, size: f.size, percentComplete: 0, amountComplete: 0, amountTotal: f.size
            }));
            const progressCallback = (_s: string, _r: number, file: any, bytesUploaded: number, bytesRemaining: number, percent: number) => {
                const entry = this.stagedFilesDisplay.find(f => f.name === file.name);
                if (entry) { entry.amountComplete = bytesUploaded; entry.amountTotal = bytesUploaded + bytesRemaining; entry.percentComplete = percent; }
            };
            const successCallback = (_s: string, _r: number, file: any, hash: any) => {
                return this.wikiService.stageAttachment(projectId, hash, {
                    fileName: file.name,
                    fileType: file.type || 'application/octet-stream'
                }).then((response: any) => {
                    const filesystemId: number = response?.filesystemId;
                    if (filesystemId) {
                        this.stagedFileIds.push(filesystemId);
                        const entry = this.stagedFilesDisplay.find(f => f.name === file.name);
                        if (entry) {
                            entry.attachmentUrl = response.location;
                            entry.filesystemId  = filesystemId;
                            entry.uploading     = false;
                        }
                    }
                });
            };
            this.uploadService.uploadFiles('wikistaged', projectId, files, { successCallback, progressCallback })
                .catch(() => this.uiService.message.showWarning(
                    this.translateService.translate('Could not upload one or more attachments'),
                    this.translateService.translate('Attachments')
                ));
        }
    }

    removeStagedFile(file: any): void {
        this.stagedFiles = this.stagedFiles.filter(f => f.name !== file.name);
        this.stagedFilesDisplay = this.stagedFilesDisplay.filter(f => f.name !== file.name);
        if (file.filesystemId) {
            this.stagedFileIds = this.stagedFileIds.filter(id => id !== file.filesystemId);
            if (this.wikiId > 0) {
                this.wikiService.deleteAttachment(this.wikiId, file.filesystemId).catch(() => {});
            }
        }
    }

    private uploadQueuedAttachments(wikiPageId: number): void {
        const pageName = _.get(this.wikiPage, 'pagename', '');
        const queuedFiles = this.attachmentQueueService.flush(pageName);
        const allFiles = [...this.stagedFiles, ...queuedFiles];
        this.stagedFiles = [];
        this.stagedFilesDisplay = [];
        this.stagedFileIds = [];
        if (!allFiles.length) { return; }

        // New page uploads — no defer, PHP assigns ref_id to the just-created version
        const successCallback = (_section: string, _refId: number, file: any, hash: any) => {
            return this.wikiService.setAttachment(wikiPageId, hash, {
                fileName: file.name,
                fileType: file.type || 'application/octet-stream'
            });
        };

        this.uploadService.uploadFiles('wikifiles', wikiPageId, allFiles, { successCallback })
            .then(() => {
                this.wikiService.getAttachments(wikiPageId)
                    .then((response: any) => {
                        const count = response?.items?.length || 0;
                        this.wikiService.attachmentCount$.next(count);
                    })
                    .catch(() => {});
            })
            .catch(() => {
                this.uiService.message.showWarning(
                    this.translateService.translate('Could not upload one or more attachments'),
                    this.translateService.translate('Attachments')
                );
            });
    }

    private deleteStagedFiles(): void {
        if (this.wikiId === 0) {
            // New page — staged files have no page ID to delete against; leave them as
            // unreferenced records (section=wikifiles, refId=NULL) for server-side GC.
            this.stagedFileIds = [];
            return;
        }
        const ids = [...this.stagedFileIds];
        this.stagedFileIds = [];
        ids.forEach(id => this.wikiService.deleteAttachment(this.wikiId, id).catch(() => {}));
    }

    private saveTagsIfChanged(wikiId?: number): Promise<void> | void {
        const tagString = this.shouldSaveTags();
        if (tagString) {
            const id = wikiId !== undefined ? wikiId : this.wikiEditorLifecycle.wikiId;
            // Update tagsOriginal synchronously so a second saveTagsIfChanged call (e.g. the
            // beforeLeaveHook firing after the afterSaveHook on a new page) sees no diff and
            // skips the redundant API call.
            const previousOriginal = _.clone(this.tagsOriginal);
            this.tagsOriginal = _.clone(this.tags);
            return this.tagsModel.save('wikipage', id, tagString)
                .then(() => {})
                .catch((err: any) => {
                    // Roll back so the next navigation retries the save, surface the error, and
                    // propagate so the lifecycle suppresses its "Changes saved" toast.
                    this.tagsOriginal = previousOriginal;
                    this.uiService.message.showError('An error occurred while saving tags');
                    return Promise.reject(err);
                });
        }
    }

    shouldSaveTags(): string | false {
        const tagString = _.map(this.tags, 'text').join(',');
        if (tagString !== _.map(this.tagsOriginal, 'text').join(',')) {
            return tagString;
        }
        return false;
    }

    hasContentChanges(): boolean {
        return this.wikiEditorLifecycle.hasContentChanges();
    }

    /** Whether leaving the editor would persist anything — content edits or staged (not-yet-claimed)
     *  attachment uploads. Hosts (e.g. `<wiki-container>`) use this to decide whether to refetch. */
    hasPendingWork(): boolean {
        return this.hasContentChanges()
            || (this.showAttachments && this.stagedFileIds.length > 0);
    }

    get saveButtonLabel(): string {
        if (this.wasConvertedFromHtml) return 'Save as Markdown';
        if (this.hasContentChanges()) return 'Save changes?';
        return 'Save';
    }

    get isWarningSave(): boolean {
        return this.wasConvertedFromHtml || this.hasContentChanges();
    }

    // ─────────────────── event handlers ────────────────────

    stringToClassColor = (tag) => {
        return this.uiService.color.stringToClassColor(tag.text);
    };

    onMarkdownSave(content: string): void {
        const extra: any = {};
        if (this.stagedFileIds.length > 0) {
            extra.attachmentIds = [...this.stagedFileIds];
            this.stagedFileIds = [];
        }
        if (this.changeSummary) {
            extra.changeSummary = this.changeSummary;
            this.changeSummary = '';
        }
        if (Object.keys(extra).length > 0) {
            this.wikiEditorLifecycle.setSavePayloadExtra(extra);
        }
        this.wikiEditorLifecycle.onMarkdownSave(content);
    }

    /**
     * Ask GForge Flow to draft a change summary from a word-level diff of the
     * page's last-saved content vs. the live editor buffer, and fill
     * `changeSummary` with the result. Never called for a brand-new page —
     * see {@link canGenerateChangeSummary}.
     */
    onGenerateChangeSummary(): void {
        if (!this.canGenerateChangeSummary || this.generatingSummary || !this.markdownEditor?.isReady) {
            return;
        }
        const before = this.wikiEditorLifecycle.contentOriginal || '';
        const after = this.markdownEditor.getMarkdown();
        const diff = this.stringDiffService.diffString(before, after);
        if (!/<ins>|<del>/.test(diff)) {
            this.generateSummaryError = this.translateService.translate('No changes to summarize yet.');
            return;
        }

        this.generatingSummary = true;
        this.generateSummaryError = null;
        this.aiFlowService.generateWikiChangeSummary(this.project?.id, diff)
            .then((result) => {
                this.changeSummary = result.summary;
            })
            .catch(() => {
                this.generateSummaryError = this.translateService.translate('Could not generate a summary. Please try again or type one in.');
            })
            .finally(() => {
                this.generatingSummary = false;
            });
    }

    onEditorCancel(): void {
        this.deleteStagedFiles();
        this.discardPendingImports();
        this.wikiEditorLifecycle.cancelEditing();
    }

    /** Remember an applied import, whose pictures stay only if the page is saved. */
    onDocumentImported(result: WikiImportResult): void {
        if (result && result.importId) {
            this.pendingImportIds.push(result.importId);
        }
    }

    /**
     * A cancelled edit keeps nothing it imported. The server keeps any picture the
     * saved page already references, and settles whatever this misses on its own
     * schedule, so a failure here costs nothing but disk space for a while.
     *
     * The ids are deliberately not forgotten when the page is saved. After a save-and-stay
     * the server has already settled those imports and answers 404 here, which is the outcome
     * wanted and is swallowed; forgetting them when Save is clicked would instead skip the
     * discard after a save that then failed (a conflict, say) and was followed by a cancel.
     */
    private discardPendingImports(): void {
        const ids = this.pendingImportIds;
        this.pendingImportIds = [];
        ids.forEach(id => this.wikiService.discardImport(this.wikiId, id).catch(() => {}));
    }

    toggleHelpPanel(): void {
        this.showHelpPanel = !this.showHelpPanel;
    }

    toggleAttachmentBar(): void {
        this.attachmentBarOpen = !this.attachmentBarOpen;
        window.localStorage.setItem(this.attachmentBarOpenStorageKey, this.attachmentBarOpen ? '1' : '0');
    }

    openAttachmentPicker(): void {
        const modalRef = this.modalService.open(WikiAttachmentPickerModalComponent, {
            backdrop: 'static',
            keyboard: true
        });
        modalRef.componentInstance.wikiPageId = this.wikiId;
        modalRef.componentInstance.stagedFiles = this.stagedFilesDisplay
            .filter((f: any) => f.attachmentUrl && !f.pending)
            .map((f: any) => ({
                fileName: f.name,
                fileSize: f.size,
                attachmentUrl: f.attachmentUrl,
                postedBy: f.postedBy,
                uploadedOn: f.uploadedOn,
            }));
        modalRef.result.then((file: any) => {
            if (!file || !file.attachmentUrl) { return; }
            if (this.markdownEditor) {
                this.markdownEditor.insertAttachment(file);
            }
        }).catch(() => {});
    }

    startHelpSidebarResize(event: MouseEvent): void {
        if (window.innerWidth < 992) return;

        event.preventDefault();
        this.isResizingHelpSidebar = true;

        const onMouseMove = (moveEvent: MouseEvent) => {
            const viewportWidth = window.innerWidth;
            const desiredWidth = viewportWidth - moveEvent.clientX;
            const maxFromViewport = Math.max(this.helpSidebarMinWidth, Math.floor(viewportWidth * 0.7));
            const clamped = Math.max(this.helpSidebarMinWidth, Math.min(desiredWidth, Math.min(this.helpSidebarMaxWidth, maxFromViewport)));
            this.helpSidebarWidth = clamped;
        };

        const onMouseUp = () => {
            this.stopHelpSidebarResize();
            this.persistHelpSidebarWidth();
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        this.removeResizeListeners = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }

    private stopHelpSidebarResize(): void {
        this.isResizingHelpSidebar = false;
        if (this.removeResizeListeners) {
            this.removeResizeListeners();
            this.removeResizeListeners = null;
        }
    }

    private restoreHelpSidebarWidth(): void {
        const storedWidth = window.localStorage.getItem(this.helpSidebarWidthStorageKey);
        const parsed = parseInt(storedWidth || '', 10);
        if (!isNaN(parsed)) {
            this.helpSidebarWidth = Math.max(this.helpSidebarMinWidth, Math.min(parsed, this.helpSidebarMaxWidth));
        }
    }

    private persistHelpSidebarWidth(): void {
        window.localStorage.setItem(this.helpSidebarWidthStorageKey, String(this.helpSidebarWidth));
    }

}
